/*
 * Copyright (c) 2020
 *    Reliant Business Valuation, LLC - http://www.reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {Report} from '../../../../model';
import {ReportNotesProvider, ReportProvider} from '../../../../providers';
import {ReportService} from '../../../../services/report.service';
import {StageService} from '../../../../services';
import {Router, ActivatedRoute} from '@angular/router';
import {ReportNote} from '../../../../model/report.note';

@Component({
  selector: 'page-report-notes',
  templateUrl: 'report.notes.page.html',
  styleUrls: ['report.notes.page.scss']
})
export class ReportNotesPage implements OnInit, OnDestroy {
  currentReport: Report = new Report();
  private reportSub: any;
  totalItems = 0;
  currentPage = 1;
  notes: ReportNote[];

  selectAll = false;
  isLoading = false;
  createDeleteFlag = false;
  showBvqNotes: boolean = false;
  bvqNotesCollapsed: boolean = true;
  pmaNotesCollapsed: boolean = true;
  bvqNotes: any = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private stageService: StageService,
    private reportNotesProvider: ReportNotesProvider
  ) {}

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport){
        this.createDeleteFlag = this.currentReport.STAGE.NAME === 'Report Submitted';
        if(this.currentReport.BVQ)
          this.checkBVQNotes();
      }
      this.loadNotes();
    });
  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
  }

  private loadNotes(): void {
    if (this.currentReport) {
      this.isLoading = true;
      this.reportNotesProvider.getNotes(this.currentReport.ID, 1, null)
        .subscribe(
          data => {
            if (data.success) {
              this.notes = ReportNote.parseArray(data.result);
            }
            this.isLoading = false;
          }, () => {
            this.isLoading = false;
          });
    }
  }

  checkBVQNotes(): void{
    Object.keys(this.currentReport.BVQ.SOURCES).map(key => {
      if(this.currentReport.BVQ.SOURCES[key].NOTE){
        this.bvqNotes.push({NOTE: this.currentReport.BVQ.SOURCES[key].NOTE});
        this.showBvqNotes = true;
      }
    });
  }

  onCreateLink(): void {
    this.reportNotesProvider.createNote(this.currentReport.ID, new ReportNote())
      .subscribe(data => {
        if (data.success) {
          this.notes.push(new ReportNote(data.result));
          this.totalItems = this.notes.length;
        }
      });
  }

  onNoteDeleted(note: ReportNote): void {
    for(let i=0; i < this.notes.length; i++){
      if(this.notes[i].ID === note.ID)
        this.notes.splice(i, 1);
    }
  }

  onOrderUp(note: ReportNote, index): void {
    this.notes.sort((a, b) => {return a.ORDER - b.ORDER;});
    if (index == 0) return; // return if first top element selected 'move up'
    this.reportNotesProvider.reorderNote(this.currentReport.ID, note, this.notes[index-1].ORDER)
      .subscribe(data => {
        if (data.success) {
          this.notes = ReportNote.parseArray(data.result);
          this.totalItems = this.notes.length;
        }
      });
  }

  onOrderDown(note: ReportNote, index): void {
    this.notes.sort((a, b) => {return a.ORDER - b.ORDER;});
    if (index == this.notes.length -1) return; // return if last bottom element selected 'move down'
    this.reportNotesProvider.reorderNote(this.currentReport.ID, note,  this.notes[index+1].ORDER)
      .subscribe(data => {
        if (data.success) {
          this.notes = ReportNote.parseArray(data.result);
          this.totalItems = this.notes.length;
        }
      });
  }

  pageChanged(event: any): void {
    if (event.page !== this.currentPage)
      this.loadNotes();
  }
}
